import { ReactNode } from "react";
import { Alert, Form } from "react-bootstrap";
import Select, { GroupBase, StylesConfig } from "react-select";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import {
  AASpinner,
  Card,
  CardGrid,
  HintIcon,
  useReactSelectTheme,
} from "@bagel-web/components";

import type { CommonAPI } from "../types";
import type { MetricUsage, UsageMetricMetadata } from "../types/Usage";
import MetricUsageSummaryReport from "./MetricUsageSummaryReport";
import MetricUsageDetailReport from "./MetricUsageDetailReport";

// Usage rollups are recomputed by a cron job roughly every minute, so caching
// the usage/metrics queries for that window avoids needless refetches.
const USAGE_STALE_TIME = 1000 * 60;

export const ORGANIZATION_SCOPE = "__organization__";

export const getYesterdayTimestamp = () =>
  moment().subtract(1, "days").format("YYYY-MM-DD");
export const getLastMonthTimestamp = () =>
  moment().subtract(1, "months").format("YYYY-MM");

type ProjectOption = {
  id: string;
  name: string;
};

type ScopeOption = {
  value: string;
  label: ReactNode;
};

type LoadableQuery<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
};

const scopeStyleOverrides: StylesConfig<
  ScopeOption,
  false,
  GroupBase<ScopeOption>
> = {
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "300px",
  }),
};

const summarySections = (
  usageRecent: MetricUsage[] | undefined,
  todayFallback: string,
  monthFallback: string
) => [
  {
    title: "Today",
    scope: "daily",
    timestamp:
      usageRecent?.find((usage) => usage.scope === "daily")?.timestamp ||
      todayFallback,
  },
  {
    title: "Month to Date",
    scope: "monthly",
    timestamp:
      usageRecent?.find((usage) => usage.scope === "monthly")?.timestamp ||
      monthFallback,
  },
];

const UsageCardLoading = () => <AASpinner />;

const UsageCards = ({
  titlePrefix,
  usageRecentQuery,
  metricsQuery,
  getCost,
}: {
  titlePrefix: string;
  usageRecentQuery: LoadableQuery<MetricUsage[]>;
  metricsQuery: LoadableQuery<UsageMetricMetadata[]>;
  getCost?: (usage: MetricUsage[] | MetricUsage) => number;
}) => {
  const metrics = metricsQuery.data ?? [];
  const isLoadingUsage = usageRecentQuery.isLoading;
  const combinedUsage = [...(usageRecentQuery.data || [])];
  const sections = summarySections(
    usageRecentQuery.data,
    moment().format("YYYY-MM-DD"),
    moment().format("YYYY-MM")
  );

  // The metric metadata determines how many cards exist, so until it loads we
  // can't render the per-metric cards — show a single placeholder instead.
  if (metricsQuery.isLoading) {
    return (
      <Card id={`${titlePrefix}-usage-loading`}>
        <UsageCardLoading />
      </Card>
    );
  }

  return (
    <>
      {metrics.map((metric) => (
        <Card
          key={`${titlePrefix}-${metric.key}`}
          id={`${titlePrefix}-${metric.key}`}
          title={metric.display_name}
        >
          <MetricUsageSummaryReport
            usage={combinedUsage}
            sections={sections}
            metric={metric}
            isLoading={isLoadingUsage}
          />
        </Card>
      ))}
      {getCost && (
        <Card
          id={`${titlePrefix}-cost`}
          title={
            <span>
              Estimated Costs
              <HintIcon className="float-end">
                Anticipated costs to date based on observed token counts
              </HintIcon>
            </span>
          }
        >
          <MetricUsageSummaryReport
            usage={combinedUsage}
            sections={sections}
            metric={{ key: "cost", display_name: "Estimated Cost" }}
            getCost={getCost}
            isLoading={isLoadingUsage}
          />
        </Card>
      )}
    </>
  );
};

const UsageScopeSection = ({
  organizationName,
  organization,
  commonAPI,
  projectsQuery,
  selectedScopeId,
  onScopeChange,
  isOrganizationScope,
  selectedProjectLabel,
  getCost,
  exportUsageDetailReport,
  quotaSlot,
  showOrganizationScope = true,
}: {
  organizationName?: string;
  // The organization identifier for usage API calls (ignored by the dashboard,
  // which operates within a single implicit organization — it passes "").
  organization: string;
  commonAPI: CommonAPI;
  projectsQuery: LoadableQuery<{ items: ProjectOption[] }>;
  selectedScopeId: string;
  onScopeChange: (scopeId: string) => void;
  isOrganizationScope: boolean;
  selectedProjectLabel: string;
  getCost?: (usage: MetricUsage[] | MetricUsage) => number;
  exportUsageDetailReport?: (
    usage: MetricUsage[],
    startDate: string,
    endDate: string,
    organization?: string
  ) => void;
  quotaSlot?: ReactNode;
  showOrganizationScope?: boolean;
}) => {
  const detailTitle = isOrganizationScope ? (
    "Organization Usage by Service Alias"
  ) : (
    <>
      <i className="bi-folder-fill me-2" />
      {selectedProjectLabel} Usage by Service Alias
    </>
  );
  const titlePrefix = isOrganizationScope
    ? "organization-usage"
    : "project-usage";

  const projectId = isOrganizationScope ? "" : selectedScopeId;
  const metricsQuery = useQuery({
    queryKey: ["usage-metrics"],
    queryFn: () => commonAPI.fetchUsageMetrics(),
    staleTime: USAGE_STALE_TIME,
  });
  const usageRecentQuery = useQuery({
    queryKey: ["usage-recent", organization, projectId],
    queryFn: () =>
      isOrganizationScope
        ? commonAPI.fetchOrganizationUsageRecent({ organization })
        : commonAPI.fetchProjectUsageRecent({ organization, projectId }),
    enabled: isOrganizationScope || !!projectId,
    staleTime: USAGE_STALE_TIME,
  });

  const projects = projectsQuery.data?.items;
  const metrics = metricsQuery.data ?? [];
  const scopeOptions: (ScopeOption | GroupBase<ScopeOption>)[] = [
    ...(showOrganizationScope
      ? [
          {
            value: ORGANIZATION_SCOPE,
            label: organizationName || "All Projects",
          },
        ]
      : []),
    {
      label: "Projects",
      options: (projects ?? []).map((project) => ({
        value: project.id,
        label: (
          <div>
            <i className="bi-folder-fill me-2" />
            {project.name}
          </div>
        ),
      })),
    },
  ];
  const selectedScopeOption = scopeOptions
    .flatMap((entry) => ("options" in entry ? entry.options : entry))
    .find((option) => option.value === selectedScopeId);

  const metadataLoadError = metricsQuery.isError || projectsQuery.isError;
  const usageLoadError = usageRecentQuery.isError;
  const usageScopeLabel = isOrganizationScope
    ? organizationName || "organization"
    : selectedProjectLabel;

  return (
    <>
      {metadataLoadError && (
        <Alert variant="warning">
          Could not load usage metadata or projects.
        </Alert>
      )}
      {usageLoadError && (
        <Alert variant="warning">
          Could not load usage data for {usageScopeLabel}.
        </Alert>
      )}

      <Form.Group className="mb-3" style={{ maxWidth: "32rem" }}>
        <Form.Label>Usage Scope</Form.Label>
        <Select<ScopeOption, false, GroupBase<ScopeOption>>
          value={selectedScopeOption ?? null}
          options={scopeOptions}
          styles={scopeStyleOverrides}
          theme={useReactSelectTheme()}
          isClearable={false}
          isLoading={projectsQuery.isLoading}
          onChange={(newValue) => {
            if (newValue) {
              onScopeChange(newValue.value);
            }
          }}
        />
      </Form.Group>

      <CardGrid>
        <UsageCards
          titlePrefix={titlePrefix}
          usageRecentQuery={usageRecentQuery}
          metricsQuery={metricsQuery}
          getCost={getCost}
        />
        <Card id={`${titlePrefix}-detail`} colSpan={4} title={detailTitle}>
          {metricsQuery.isLoading ? (
            <UsageCardLoading />
          ) : (
            <MetricUsageDetailReport
              commonAPI={commonAPI}
              organization={organization}
              projectId={projectId}
              organizationName={organizationName}
              metrics={metrics}
              getCost={getCost}
              exportUsageDetailReport={exportUsageDetailReport}
            />
          )}
        </Card>
        {!isOrganizationScope && quotaSlot}
      </CardGrid>
    </>
  );
};

export default UsageScopeSection;
